/**
 * SampleCall - the playable scripted sample call in the hero status panel.
 *
 * A short scripted booking call plays as timed captions with a small live
 * waveform. If /sample-call.mp3 exists in public/, the same captions sync to
 * the real audio instead (drop the file in; no code change). Clearly labeled
 * a sample. Reduced motion / prerender: the full transcript renders statically.
 */

import { useEffect, useRef, useState } from 'react'
import { Pause, Play } from 'lucide-react'
import { useReducedMotion } from '@/components/motion'
import { isPrerender } from '@/utils/prerender'

interface Line {
  who: 'Caller' | 'Reeva'
  text: string
  /** caption start time, seconds */
  t: number
}

const SCRIPT: Line[] = [
  { who: 'Caller', t: 0, text: 'Hi, do you have anything open tomorrow morning?' },
  { who: 'Reeva', t: 2.8, text: 'Good morning! We have 9:30 or 11 AM. Which works better?' },
  { who: 'Caller', t: 6.4, text: "9:30, please. It's for a cleaning." },
  { who: 'Reeva', t: 9.0, text: 'Perfect. Can I get your name and a good number?' },
  { who: 'Caller', t: 11.8, text: 'Sam Rivera, 720-555-0143.' },
  { who: 'Reeva', t: 14.6, text: "You're booked for 9:30 tomorrow, Sam. I'll text you a confirmation right now." },
]
const DURATION = 19

export function SampleCall() {
  const prefersReducedMotion = useReducedMotion()
  const staticMode = prefersReducedMotion || isPrerender()
  const [playing, setPlaying] = useState(false)
  const [clock, setClock] = useState(0)
  const [hasAudio, setHasAudio] = useState(false)
  const audioRef = useRef<HTMLAudioElement>(null)
  const rafRef = useRef<number>(0)
  const startRef = useRef(0)

  useEffect(() => {
    // Audio is optional: light the sound path up only if the asset exists.
    fetch('/sample-call.mp3', { method: 'HEAD' })
      .then((r) => setHasAudio(r.ok && (r.headers.get('content-type') || '').includes('audio')))
      .catch(() => setHasAudio(false))
  }, [])

  useEffect(() => {
    if (!playing || hasAudio) return
    const tick = (now: number) => {
      const t = (now - startRef.current) / 1000
      if (t >= DURATION) {
        setClock(DURATION)
        setPlaying(false)
        return
      }
      setClock(t)
      rafRef.current = requestAnimationFrame(tick)
    }
    startRef.current = performance.now() - clock * 1000
    rafRef.current = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(rafRef.current)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing, hasAudio])

  const toggle = () => {
    if (hasAudio && audioRef.current) {
      if (playing) audioRef.current.pause()
      else {
        if (clock >= DURATION) audioRef.current.currentTime = 0
        void audioRef.current.play()
      }
      setPlaying(!playing)
      return
    }
    if (!playing && clock >= DURATION) setClock(0)
    setPlaying(!playing)
  }

  const done = !playing && clock >= DURATION

  if (staticMode) {
    return (
      <div className="sample-call">
        <p className="home-status-sample-label">Sample call</p>
        <ul className="sample-call-lines">
          {SCRIPT.map((l, i) => (
            <li key={i} className={`sample-call-line sample-call-line--on sample-call-line--${l.who.toLowerCase()}`}>
              <span className="sample-call-who">{l.who}</span> {l.text}
            </li>
          ))}
        </ul>
      </div>
    )
  }

  return (
    <div className="sample-call">
      <div className="sample-call-head">
        <p className="home-status-sample-label">Sample call</p>
        <button type="button" className="sample-call-play" onClick={toggle} aria-label={playing ? 'Pause the sample call' : 'Play the sample call'}>
          {playing ? <Pause size={12} /> : <Play size={12} />}
          {playing ? 'Pause' : done ? 'Replay' : hasAudio ? 'Hear it' : 'Play'}
        </button>
        <span className={`sample-call-bars${playing ? ' rw-anim sample-call-bars--live' : ''}`} aria-hidden="true">
          <span /><span /><span /><span /><span />
        </span>
      </div>

      {hasAudio && (
        <audio ref={audioRef} src="/sample-call.mp3" preload="none" onTimeUpdate={(e) => setClock(e.currentTarget.currentTime)} onEnded={() => setPlaying(false)} />
      )}

      {/* All lines are always in the layout (no reflow while playing); each
          fades in at its cue. */}
      <ul className="sample-call-lines" aria-live="polite">
        {SCRIPT.map((l, i) => (
          <li
            key={i}
            className={`sample-call-line sample-call-line--${l.who.toLowerCase()}${l.t <= clock || (i === 0 && !playing && clock === 0) ? ' sample-call-line--on' : ''}`}
          >
            <span className="sample-call-who">{l.who}</span> {l.text}
          </li>
        ))}
      </ul>
    </div>
  )
}
